import { getApiOrigin } from '@/features/api/utils/api-origin.util';

// Constants for log levels
const _LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
} as const;

// Type for log data
type LogData = Record<string, unknown>;

// Interface for log entries
interface LogEntry {
  timestamp: string;
  level: keyof typeof _LOG_LEVELS;
  module: string;
  message: string;
  data?: LogData;
  debugId?: string;
  caller?: string;
  file: string;
  line: number;
}

/**
 * Logger utility for consistent logging across the application
 * Supports debug filtering and dev server logging
 */
class Logger {
  //private currentDebugModule: string | null = import.meta.env.VITE_DEBUG_MODULE || null;
  private isProduction: boolean = import.meta.env?.VITE_ENV === 'production' || false;
  private isStaging: boolean =
    import.meta.env?.VITE_ENV === 'staging' || import.meta.env?.VITE_ENV === 'ci' || false;
  private isDevelopment: boolean = import.meta.env?.VITE_ENV === 'development' || false;

  constructor() {
    // Initialization - nothing to log directly to console now
  }

  /**
   * Get caller information for debugging
   */
  private getCaller(): { caller: string; file: string; line: number } {
    try {
      const err = new Error();
      const stack = err.stack || '';
      const stackLines = stack.split('\n');

      // Find the first non-logger file in the stack
      for (let i = 0; i < stackLines.length; i++) {
        const callerLine = stackLines[i].trim();

        // Skip if this is still in the logger file
        if (callerLine.includes('logger.ts')) {
          continue;
        }

        // Extract file and line information using regex
        const match =
          callerLine.match(/at\s+.+\s+\((.+):(\d+):(\d+)\)/) ||
          callerLine.match(/at\s+(.+):(\d+):(\d+)/);

        if (match) {
          // Get just the filename without path or query params
          let fullPath = match[1];

          // Strip query parameters if they exist
          if (fullPath.includes('?')) {
            fullPath = fullPath.split('?')[0];
          }

          // Extract just the filename
          const pathParts = fullPath.split('/');
          const file = pathParts[pathParts.length - 1];
          const line = parseInt(match[2], 10);

          return {
            caller: callerLine,
            file,
            line,
          };
        }
      }

      // Fallback if no suitable caller found
      return {
        caller: 'Unknown',
        file: 'unknown',
        line: 0,
      };
    } catch {
      return {
        caller: 'Unknown',
        file: 'unknown',
        line: 0,
      };
    }
  }

  /**
   * Send log entry to backend server in all environments
   */
  private async sendToServer(entry: LogEntry): Promise<void> {
    try {
      // Only send to server if we're in a browser environment
      if (typeof window !== 'undefined') {
        // Use different API URLs and endpoints based on environment
        let apiUrl: string;
        let serverUrl: string;

        // Use consistent logging endpoint across all environments
        apiUrl = getApiOrigin();
        serverUrl = `${apiUrl}/api/logs/log`;

        // Add a unique request ID to help track issues with log delivery
        const requestId = `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Ensure file and line are included from the entry
        const payload = {
          ...entry,
          requestId,
          source: 'frontend',
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: entry.timestamp || new Date().toISOString(),
          // Explicitly include file and line to ensure they're passed to backend
          file: entry.file || 'unknown',
          line: entry.line || 0,
          environment: this.isProduction
            ? 'production'
            : this.isStaging
              ? 'staging'
              : 'development',
        };

        // Send quietly without logging to console for success
        const response = await fetch(serverUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        // Only log errors to console
        if (!response.ok) {
          console.error(
            `[Logger] Failed to send log to server: ${response.status} ${response.statusText}`,
            { entry, requestId }
          );
        }
      }
    } catch (error) {
      // Log server communication errors to console
      console.error('[Logger] Error sending log to server:', error);
    }
  }

  /**
   * Log a message with the specified level
   */
  private log(
    level: keyof typeof _LOG_LEVELS,
    module: string,
    message: string,
    data?: LogData,
    debugId?: string
  ): void {
    const timestamp = new Date().toISOString();
    const callerInfo = this.getCaller();

    const entry: LogEntry = {
      timestamp,
      level,
      module,
      message,
      data,
      debugId,
      caller: callerInfo.caller,
      file: callerInfo.file,
      line: callerInfo.line,
    };

    // Always send to server in all environments
    this.sendToServer(entry);
  }

  // Public logging methods
  debug(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('DEBUG', module, message, data, debugId);
  }

  info(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, message, data, debugId);
  }

  warn(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('WARN', module, message, data, debugId);
  }

  error(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('ERROR', module, message, data, debugId);
  }

  // Special logging methods with emoji indicators
  permission(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🔒 ${message}`, data, debugId);
  }

  success(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `✅ ${message}`, data, debugId);
  }

  database(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `📥 ${message}`, data, debugId);
  }

  token(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🎟️ ${message}`, data, debugId);
  }

  url(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🌐 ${message}`, data, debugId);
  }

  refresh(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🔄 ${message}`, data, debugId);
  }

  server(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🚀 ${message}`, data, debugId);
  }

  init(module: string, message: string, data?: LogData, debugId?: string): void {
    this.log('INFO', module, `🔧 ${message}`, data, debugId);
  }
}

// Export a singleton instance
export const logger = new Logger();
